import { useState } from 'react';
import type { AlignmentFlag, AlignmentRulingValue, RoleRecord } from '../lib/api';
import { isCheckAgainBusy, resolveAlignmentReviewDisplayState } from '../lib/alignmentReviewDisplay';
import {
  groupAlignmentFlagsByRole,
  sectionCountLabel,
  sectionKey,
} from '../lib/alignmentReviewGrouping';
import {
  completionSummaryCopy,
  computeAlignmentReviewProgress,
  progressLineCopy,
  progressPercent,
  tallySettledRulings,
  type SettledRuling,
} from '../lib/alignmentReviewProgress';
import type { AlignmentReviewEntitlementBlock } from '../lib/useAlignmentReview';
import { AlignmentReviewCard } from './AlignmentReviewCard';
import { Card } from './Card';
import styles from './AlignmentReviewDeck.module.css';

export interface AlignmentReviewDeckProps {
  flags: AlignmentFlag[];
  roles: RoleRecord[];
  /** True on the first fetch AND on every manual re-check (useAlignmentReview's
   *  `state.loading`) — the deck's own honest "checking" state, distinct from
   *  a card's per-achievement `saving` (pendingAchievementId). */
  loading: boolean;
  /** Present only when the last fetch failed as a genuine error (network/
   *  server) — mirrors Refine.tsx's existing canvasError/rehomeError single-
   *  honest-surface pattern. Mutually exclusive with `entitlementBlock`: a
   *  402 renders as the blocked note below, never as this generic string. */
  fetchError: string | null;
  /** G1 (docs/CHANGE-CONTROL.md R184) — present when GET /corpus/alignment-
   *  review was blocked by requireEntitlement('tailoring'). Renders the
   *  SERVED tier/reason/hint verbatim (no-frontend-bandaids: the backend
   *  explains itself), mirroring JobDetail.tsx's EntitlementBlockedNote copy
   *  conventions but in Mira's own first-person voice — this deck is never
   *  silently absent for a free-tier user, it tells them exactly why. */
  entitlementBlock: AlignmentReviewEntitlementBlock | null;
  /** The achievementId currently mid-ruling (its card shows a saving state;
   *  every OTHER card stays interactive — mirrors dragRehome's "one card at a
   *  time" note, not a whole-deck lock). */
  pendingAchievementId: string | null;
  onMove: (achievementId: string, roleId: string) => void;
  onKeep: (achievementId: string) => void;
  onFlagSplit: (achievementId: string) => void;
  /** RESUME-POLISH lane, part c: MERGE this card into `survivorId` (the
   *  richest cluster member, computed by AlignmentReviewCard the same way
   *  the résumé composer picks a survivor). Only ever called for a card
   *  carrying a 'near_duplicate' signal. */
  onMerge: (achievementId: string, survivorId: string) => void;
  /** The shared ruling-error banner (mirrors Refine.tsx's existing
   *  canvasError / rehomeError pattern) — ONE honest error surface for any
   *  ruling failure, not a per-card duplicate. Distinct from `fetchError`
   *  (a ruling failure vs. a review-fetch failure are different events). */
  error: string | null;
  /** The re-run affordance's own handler (useAlignmentReview's `refresh`) —
   *  fired by BOTH the flags-exist header's "Check again" control and the
   *  zero-flags resting state's own tap, so there is exactly one refetch
   *  path regardless of which render branch the user is looking at. */
  onCheckAgain: () => void;
  /** CREW-UX — the completion moment's "Want to see the résumé fresh?" next
   *  step: switches Refine.tsx's `corpusView` to 'resume' and scrolls to it.
   *  The résumé view already lives on this same page (no new route) — this
   *  deck only asks its parent to reveal it, per the no-frontend-bandaids
   *  split (the deck itself owns no view-toggle state). */
  onViewResume: () => void;
}

/** G1 — the honest, SPECIFIC line for each real ResolvedEntitlement.reason
 *  the server can serve on a 402, mirroring JobDetail.tsx's
 *  describeEntitlementReason exactly (same vocabulary, same "no-frontend-
 *  bandaids: describe the served reason, never invent a different one"
 *  discipline) — kept as its own small copy of that switch rather than a
 *  cross-file import, since AlignmentReviewDeck is deliberately
 *  self-contained (see this file's own header: pure presentation, no
 *  cross-page coupling). */
function describeEntitlementReason(reason: string): string {
  switch (reason) {
    case 'trial_expired':
      return "Your free trial's wrapped up.";
    case 'past_due_lapsed':
      return 'Your last payment didn’t go through, and the grace period’s run out too.';
    case 'canceled':
      return 'Your subscription’s been canceled.';
    case 'no_subscription':
      return "Checking your stories against their roles is part of tailoring — want to start a trial?";
    default:
      return "Checking your stories against their roles is part of tailoring — want to start a trial?";
  }
}

/** One flagged achievement's ruling just landed, transiently — the calm,
 *  non-confetti acknowledgment (the brief: "a small, calm confirmation on
 *  each ruling... transient, not confetti"). Auto-clears after a few seconds
 *  via the caller's own timer (see the deck's handleRuled below); this
 *  component only renders whatever line it is currently given. Respects
 *  prefers-reduced-motion via the SAME global collapse globals.css already
 *  applies to every animation/transition duration — no local media query
 *  needed (mirrors SideNavDrawer.module.css / CoachMark.module.css's own
 *  note), so under reduced motion this simply appears/disappears without the
 *  fade instead of animating it. */
function RulingAcknowledgment({ text }: { text: string }) {
  return (
    <div className={styles.ackRow} role="status" aria-live="polite">
      {text}
    </div>
  );
}

/** The exact acknowledgment line for a just-landed ruling — one honest
 *  sentence per ruling kind, never a generic "Saved!" that doesn't reflect
 *  what the user actually chose. */
function acknowledgmentFor(ruling: AlignmentRulingValue): string {
  switch (ruling) {
    case 'moved':
      return "Settled — that one's moved for good.";
    case 'kept':
      return "Settled — that one's staying put, for good.";
    case 'flagged_split':
      return 'Settled — flagged for a closer look later.';
    case 'merged':
      return "Settled — merged into the richer telling, for good.";
    default:
      return "Settled — that one's done for good.";
  }
}

/** One collapsible, by-role section of the deck (CREW-UX). Collapsed by
 *  default (see AlignmentReviewDeck's own expandedKey state) — the header
 *  alone (role title + company + an honest count chip) is the whole
 *  first-paint footprint; expanding reveals this section's own cards.
 *  ACCORDION CHOICE: expanding one section collapses whichever OTHER section
 *  was open (single-open accordion) rather than allowing many open at once —
 *  see AlignmentReviewDeck's own doc comment for why this is the calmer
 *  behavior for the exact problem being solved here (a daunting WALL of
 *  cards). */
function AlignmentReviewSectionRow({
  roleLabel,
  count,
  expanded,
  onToggle,
  children,
}: {
  roleLabel: { company: string; title: string } | null;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const title = roleLabel ? `${roleLabel.company} — ${roleLabel.title}` : 'Unattached';
  return (
    <div className={styles.section}>
      <button
        type="button"
        className={styles.sectionHeader}
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          className={expanded ? `${styles.chevron} ${styles.chevronOpen}` : styles.chevron}
        >
          <path
            d="M9 6l6 6-6 6"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className={styles.sectionTitle}>{title}</span>
        <span className={styles.sectionCountChip}>{sectionCountLabel(count)}</span>
      </button>
      {expanded ? <div className={styles.sectionBody}>{children}</div> : null}
    </div>
  );
}

/**
 * "Meira noticed N stories that might be filed under the wrong role" — the
 * review deck's entry card, and (CLEANUP-CREW's gating pass) the ONE place
 * this whole capability surfaces on Refine, in every state. WHICH state is
 * a pure decision (resolveAlignmentReviewDisplayState, alignmentReviewDisplay
 * .ts) this component only renders — the same "pure core, thin presentation"
 * split refineThreadsSource.ts/refineDnaReveal.ts already establish for this
 * page's other panels:
 *
 *   - 'blocked' (requireEntitlement('tailoring') 402'd): an honest "here's
 *     why, here's the fix" note — never a silent absence standing in for a
 *     real paid-feature gate.
 *   - 'checking' (loading with nothing to show yet): "Checking your
 *     stories…" — the first-load/empty-corpus honest wait state.
 *   - 'resting' (zero open flags, not loading): a one-line Mira-voice
 *     resting state ("Your stories all look properly filed…") with its own
 *     "Check again" tap — the surface is now visibly THERE even with zero
 *     open flags, never invisible-by-omission the way it was before.
 *   - 'list' (one or more open flags): the CREW-UX collapsible, by-role deck
 *     (see below), PLUS a small "Check again" control in the header (the
 *     re-run affordance) — a re-check that fires while flags are already
 *     showing busies this control rather than yanking the visible cards into
 *     a loading wall.
 *
 * CREW-UX (the owner, live on his phone, verbatim): "Scrolling past the
 * daunting task of reviewing all the story items... I'll probably just give
 * up and walk away... collapsing the sections will help... I also need some
 * sort of encouragement to work through them slowly and that it's gonna be
 * worth it... show them the results." This pass adds, on top of the existing
 * gating states:
 *
 *   1. COLLAPSIBLE SECTIONS grouped BY ROLE (alignmentReviewGrouping.ts) —
 *      each collapsed by default; a short list of role headers is the whole
 *      first paint. ACCORDION: expanding one section collapses whichever
 *      other was open (component state, session-only — no persistence for
 *      v1 per this lane's own brief). WHY single-open over independently-
 *      toggleable: the stated problem is volume-anxiety ("I'll probably just
 *      give up") — a single-open accordion guarantees the user is never
 *      looking at more than one section's worth of cards at once, which is
 *      the strongest form of "the wall is gone," whereas independently-
 *      toggleable sections could still let every section end up open
 *      simultaneously and rebuild the same wall one tap at a time.
 *   2. THE ENCOURAGEMENT ARCHITECTURE (alignmentReviewProgress.ts) — a warm,
 *      Mira-voice header framing WHY this matters, a real "X settled · Y to
 *      go" progress line + bar (session-derived, NO INVENTION: settledCount
 *      only ever counts rulings THIS session actually completed), a quiet
 *      pacing-permission line, a transient calm acknowledgment per ruling,
 *      and — the payoff — a completion moment once every flag in view is
 *      settled, with REAL merged/confirmed counts and a link to the résumé
 *      view already on this same page (no new route).
 *
 * Expands to one AlignmentReviewCard per flag when any exist. This component
 * is pure presentation (the no-frontend-bandaids law): it owns no fetch, no
 * write, no ruling-decision logic beyond tracking which rulings landed THIS
 * session for the encouragement copy (a thin, purely-additive observation of
 * its own already-forwarded callbacks, not a new source of truth) — the
 * fetch, the write, and the terminal-ruling state machine all still live in
 * useAlignmentReview (the hook), alignmentReview.ts (the ruling state
 * machine), and alignmentReviewDisplay.ts (the display-state decision).
 */
export function AlignmentReviewDeck({
  flags,
  roles,
  loading,
  fetchError,
  entitlementBlock,
  pendingAchievementId,
  onMove,
  onKeep,
  onFlagSplit,
  onMerge,
  error,
  onCheckAgain,
  onViewResume,
}: AlignmentReviewDeckProps) {
  const displayState = resolveAlignmentReviewDisplayState(loading, flags.length, entitlementBlock);
  const checkAgainBusy = isCheckAgainBusy(loading);

  // Collapsible sections (CREW-UX #1): which section key is currently open,
  // or null when every section is collapsed. Component state, session-only —
  // no persistence promised for v1 (this lane's own brief). Single-open
  // accordion: setting this to a new key implicitly collapses whichever
  // other section was open, since only ONE key can ever be stored here.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  // THE ENCOURAGEMENT ARCHITECTURE's real, session-local ledger of every
  // ruling that has actually landed so far (alignmentReviewProgress.ts's
  // SettledRuling shape) — grows by exactly one entry each time this deck
  // forwards a terminal ruling upward via handleRuled below. NEVER
  // reconciled against the server's own history; a page refresh honestly
  // resets to zero-settled, matching this lane's "component state; no
  // persistence needed v1" instruction.
  const [settled, setSettled] = useState<SettledRuling[]>([]);
  // The most recent ruling's transient acknowledgment line, or null when
  // nothing is currently showing. Cleared by its own short timer in
  // handleRuled — a calm confirmation, not a permanent log entry.
  const [ackText, setAckText] = useState<string | null>(null);

  /** Records a just-landed ruling (CREW-UX #2) and shows its calm transient
   *  acknowledgment, THEN forwards the original call upward unchanged — this
   *  deck adds an observation, it never intercepts or delays the real write
   *  (that still happens exactly as before, in Refine.tsx's handlers via the
   *  hook). Real, grounded data only: this fires with the SAME achievementId
   *  + ruling kind the user's own tap selected, nothing re-derived. */
  function handleRuled(achievementId: string, ruling: AlignmentRulingValue, forward: () => void) {
    setSettled((cur) => [...cur, { achievementId, ruling }]);
    setAckText(acknowledgmentFor(ruling));
    window.setTimeout(() => setAckText(null), 3200);
    forward();
  }

  if (displayState === 'blocked' && entitlementBlock) {
    return (
      <Card>
        <div className={styles.header}>
          <span className={styles.dot} />
          <div className={styles.title}>{describeEntitlementReason(entitlementBlock.reason)}</div>
        </div>
        <div className={styles.restingBody}>
          {entitlementBlock.hint === 'start your trial'
            ? "Start your free trial and I'll keep an eye on which stories belong where."
            : entitlementBlock.hint}
        </div>
      </Card>
    );
  }

  if (displayState === 'checking' || displayState === 'resting') {
    return (
      <Card>
        <div className={styles.header}>
          <span className={styles.dot} />
          <div className={styles.title}>
            {displayState === 'checking' ? 'Checking your stories…' : 'Your stories all look properly filed'}
          </div>
        </div>
        {displayState === 'resting' ? (
          <div className={styles.restingBody}>I'll keep checking as new ones land.</div>
        ) : null}
        <button
          type="button"
          className={styles.checkAgainButton}
          onClick={onCheckAgain}
          disabled={checkAgainBusy}
        >
          {checkAgainBusy ? <span className={styles.spinner} aria-hidden="true" /> : null}
          Check again
        </button>
        {fetchError ? (
          <div className={styles.errorRow} role="alert">
            {fetchError}
          </div>
        ) : null}
      </Card>
    );
  }

  const sections = groupAlignmentFlagsByRole(flags);
  const progress = computeAlignmentReviewProgress(settled, flags.length);
  const showProgress = progress.totalInView > 0;

  return (
    <Card>
      <div className={styles.header}>
        <span className={styles.dot} />
        <div className={styles.title}>
          Meira noticed {flags.length} {flags.length === 1 ? 'story' : 'stories'} that might be
          filed under the wrong role
        </div>
        <button
          type="button"
          className={styles.checkAgainLink}
          onClick={onCheckAgain}
          disabled={checkAgainBusy}
          aria-label="Check my stories again"
        >
          {checkAgainBusy ? <span className={styles.spinner} aria-hidden="true" /> : null}
          {checkAgainBusy ? 'Checking…' : 'Check again'}
        </button>
      </div>

      {/* CREW-UX #2 — THE WHY, before anything else: the frame that makes the
          list ahead feel worth doing rather than a chore, plus (below) the
          pacing permission that makes going slowly legitimate. Renders once,
          above every section, regardless of how many sections are open. */}
      <div className={styles.encourageIntro}>
        These are the addresses and duplicate tellings in your story — each one you settle makes
        every résumé, letter, and essay I write for you sharper. No rush. They keep.
      </div>

      {showProgress ? (
        <div className={styles.progressBlock}>
          <div className={styles.progressLine}>{progressLineCopy(progress)}</div>
          <div
            className={styles.progressTrack}
            role="progressbar"
            aria-valuenow={progressPercent(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className={styles.progressFill} style={{ width: `${progressPercent(progress)}%` }} />
          </div>
        </div>
      ) : null}

      <div className={styles.pacingLine}>
        Even one or two at a time moves your story forward — I'll keep the rest safe.
      </div>

      {ackText ? <RulingAcknowledgment text={ackText} /> : null}

      {progress.allSettledThisSession ? (
        // THE RESULTS (CREW-UX's stated payoff) — every flag in view has been
        // settled THIS session. Real tallied counts only; see
        // completionSummaryCopy's own doc comment for the exact NO INVENTION
        // wording rules.
        <div className={styles.completionBlock}>
          <div className={styles.completionHeadline}>
            {completionSummaryCopy(tallySettledRulings(settled))}
          </div>
          <button type="button" className={styles.completionCta} onClick={onViewResume}>
            Want to see the résumé fresh?
          </button>
        </div>
      ) : (
        sections.map((section) => {
          const key = sectionKey(section.roleId);
          const expanded = expandedKey === key;
          return (
            <AlignmentReviewSectionRow
              key={key}
              roleLabel={section.roleLabel}
              count={section.flags.length}
              expanded={expanded}
              onToggle={() => setExpandedKey((cur) => (cur === key ? null : key))}
            >
              {section.flags.map((flag) => (
                <AlignmentReviewCard
                  key={flag.achievementId}
                  flag={flag}
                  roles={roles}
                  saving={pendingAchievementId === flag.achievementId}
                  onMove={(roleId) =>
                    handleRuled(flag.achievementId, 'moved', () => onMove(flag.achievementId, roleId))
                  }
                  onKeep={() => handleRuled(flag.achievementId, 'kept', () => onKeep(flag.achievementId))}
                  onFlagSplit={() =>
                    handleRuled(flag.achievementId, 'flagged_split', () => onFlagSplit(flag.achievementId))
                  }
                  onMerge={(survivorId) =>
                    handleRuled(flag.achievementId, 'merged', () => onMerge(flag.achievementId, survivorId))
                  }
                />
              ))}
            </AlignmentReviewSectionRow>
          );
        })
      )}

      {fetchError ? (
        <div className={styles.errorRow} role="alert">
          {fetchError}
        </div>
      ) : null}
      {error ? (
        <div className={styles.errorRow} role="alert">
          {error}
        </div>
      ) : null}
    </Card>
  );
}
